import { INIT_DEBUGGER } from '../../core/debug/debugMacros';

import { action, computed, IObservableArray, makeObservable, observable } from 'mobx';
import HLS, { Fragment, type MediaPlaylist as HlsAudioTrack } from 'hls.js';

import { AsyncResult, Maybe } from '../../core';
import { type MediaInfo, type JobModel } from '../../entities';
import { BindingProps, StoreNode } from '../../store';
import { Store } from '../../store/store';
import { IPlayerAdapter } from '../player';
import { HTMLMediaNetworkState, HTMLMediaReadyState } from './htmlMediaSchema';
import { getBufferedTimeFromRanges, getSeekableTimeFromRanges, getSeekableWindow, timeRangesHas } from '../../core/media/mediaUtils';
import { ITimeRegion } from '../../core/time/timeSchema';
import { createArray } from '../../core/array';
import { HlsManifestStatus, SeekParams, SetAutoplayParams, SetControlsParams, SetPlaybackRateParams, SetVolumeParams } from './playerAdapterSchema';
import { PlayerAudioTracksState } from './playerAudioTracksState';

import { syncState, handleExternalCaptions, handleDOMEvent, mountVideoElement, unmountVideoElement, reattachDOMVideo as reattachDOMVideoFn, tryInitPlayer, isValidSeekTime as isValidSeekTimeFn, playMedia, pauseMedia, seekMedia, setMediaVolume, setMediaPlaybackRate, setMediaControls, setMediaAutoplay, invokeMediaAction, VIDEO_DOM_EVENTS } from './playerMediaState';

export { VIDEO_DOM_EVENTS };
export { HlsManifestStatus };

type Props = {
  media: MediaInfo,
  jobId: string | null,
};

export class PlayerAdapterState
  extends StoreNode
  implements IPlayerAdapter {

  readonly nodeType: 'PlayerAdapter' = 'PlayerAdapter';

  constructor(store: Store, props?: BindingProps<Props>) {
    super(store, props);
    makeObservable(this);

    INIT_DEBUGGER(this, {
      color: 'purple',
      enableTrace: false
    });

    this.onPropChange('media',
      this.handleMediaChange);

    this.playerAudioTracks = new PlayerAudioTracksState(this.store, {
      adapter: this
    });
  }

  readonly playerAudioTracks: PlayerAudioTracksState;

  @computed get media(): MediaInfo | null {
    return this.resolvedProps.media;
  }

  @computed get jobId(): string {
    return this.resolvedProps.jobId;
  }

  @computed get job(): JobModel | null {
    return this.store.maybeGetJob(this.jobId);
  }

  @observable isInitialized = false;
  readonly fragments: IObservableArray<Fragment> = createArray<Fragment>(true) as IObservableArray;
  @observable currentFragment: Fragment | null = null;
  @observable initFragment: Fragment | null = null;
  @observable fragmentDuration: number | null = null;
  @observable initPTS: number | null = null;
  @observable isLiveStream: boolean = false;

  @observable videoElement: HTMLVideoElement | null = null;

  @observable duration: number = 0;
  @computed get hasDuration() {
    return (Number.isFinite(this.duration) && this.duration > 0);
  }

  @observable error: MediaError | null = null;
  @observable readyState: HTMLMediaReadyState = HTMLMediaReadyState.HAVE_NOTHING;
  @observable networkState: HTMLMediaNetworkState = HTMLMediaNetworkState.NETWORK_EMPTY;

  @observable controls: boolean = false;
  @observable autoplay: boolean = false;
  @observable autoplayAfterRetry: boolean = false;
  @observable currentTime: number = 0;
  @observable volume: number = 0;
  @observable isMuted: boolean = false;
  @observable playbackRate: number = 1.0;

  @observable playedTimeRanges: TimeRanges | null = null;
  @observable bufferedTimeRanges: TimeRanges | null = null;
  @observable seekableTimeRanges: TimeRanges | null = null;
  @observable isPaused = false;
  @observable isSeeking: boolean = false;
  @observable isReady: boolean = false;
  @observable timer: NodeJS.Timeout | null = null;

  @computed get bufferedTime(): number {
    return getBufferedTimeFromRanges(this.bufferedTimeRanges, this.currentTime) ?? 0;
  }

  @computed get seekableTime(): number {
    return getSeekableTimeFromRanges(this.seekableTimeRanges, this.currentTime) ?? 0;
  }

  @computed get seekableWindow(): ITimeRegion | null {
    return getSeekableWindow(this.seekableTimeRanges);
  }

  @computed get seekableStart(): number {
    return this.seekableWindow?.startTime ?? 0;
  }

  @computed get seekableEnd(): number {
    return this.seekableWindow?.endTime ?? 0;
  }

  @computed get initPtsInSec(): number {
    if (this.initPTS)
      return this.initPTS / 90000;

    return 0;
  }

  @observable isEnded: boolean = false;

  @observable hlsManifestStatus: HlsManifestStatus = HlsManifestStatus.None;

  @computed get isHlsManifestParsed(): boolean {
    return this.hlsManifestStatus === HlsManifestStatus.Parsed;
  }

  @computed get isHlsManifestLoaded(): boolean {
    return [HlsManifestStatus.Loaded, HlsManifestStatus.Parsed].includes(this.hlsManifestStatus);
  }

  @computed get isHlsManifestError(): boolean {
    return this.hlsManifestStatus === HlsManifestStatus.Error;
  }

  @computed get isHlsManifestIdle(): boolean {
    return this.hlsManifestStatus === HlsManifestStatus.None;
  }

  @observable clientWidth: number | null = null;
  @observable clientHeight: number | null = null;
  @observable videoWidth: number | null = null;
  @observable videoHeight: number | null = null;

  @observable hls: HLS | null = null;

  @observable initialCurrentTime: number | null = null;
  @observable initialVolume: number | null = null;
  @observable initialPlaybackRate: number | null = null;
  @observable initialIsPlaying: boolean | null = null;
  @observable initialControls: boolean | null = null;
  @observable initialAutoplay: boolean | null = null;

  @computed get isWaiting(): boolean {
    if (this.readyState === null)
      return false;

    return (
      this.readyState <= HTMLMediaReadyState.HAVE_CURRENT_DATA &&
      !this.isPaused && (
        this.isSeeking ||
        !timeRangesHas(this.bufferedTimeRanges, this.currentTime)));
  }

  @computed get isMetadataLoaded(): boolean {
    return this.readyState >= HTMLMediaReadyState.HAVE_METADATA;
  }

  @computed get canInvokePlay(): boolean {
    return this.isMetadataLoaded && !!this.videoElement;
  }

  @computed get isPlaying(): boolean {
    return (
      !!this.videoElement &&
      !this.isPaused);
  }

  @computed get isActuallyPlaying(): boolean {
    return (
      !!this.videoElement &&
      this.readyState >= HTMLMediaReadyState.HAVE_FUTURE_DATA &&
      !this.isPaused);
  }

  get canPlay() {
    if (this.readyState === null)
      return false;
    return this.readyState >= HTMLMediaReadyState.HAVE_FUTURE_DATA;
  }

  get canPlayThrough() {
    if (this.readyState === null)
      return false;
    return this.readyState >= HTMLMediaReadyState.HAVE_ENOUGH_DATA;
  }

  // Bound event handler for DOM events (used by mount/unmount)
  @action
  handleDOMEventBound = (evt: Event) => {
    handleDOMEvent(this, evt);
  }

  @action
  syncStateFromDom() {
    syncState(this);
  }

  @action
  private handleMediaChange = () => {
    if (this.isInitialized && !this.isHlsManifestLoaded) {
      this.reattachDOMVideo();
    }
    else
      tryInitPlayer(this);
  }

  @action
  mounted(video: HTMLVideoElement | null) {
    mountVideoElement(this, video!);
  }

  @action
  unmounted() {
    unmountVideoElement(this);
  }

  @action
  reattachDOMVideo() {
    reattachDOMVideoFn(this);
  }

  setHlsAudioTrack = (track: HlsAudioTrack) => {
    if (!this.hls)
      return;

    this.hls.audioTrack = track.id;
  }

  isValidSeekTime(time?: Maybe<number>): boolean {
    return isValidSeekTimeFn(this, time);
  }

  @action
  handleExternalCaptions = (video: HTMLVideoElement) => {
    handleExternalCaptions(this, video);
  }

  @action
  invoke = async (type: string, payload?: unknown) => {
    await invokeMediaAction(this, type, payload as SeekParams | SetVolumeParams | SetPlaybackRateParams | SetControlsParams | SetAutoplayParams | null);
  }

  // #region Media API

  @action
  async play(): AsyncResult<boolean> {
    return playMedia(this);
  }

  @action
  pause() {
    pauseMedia(this);
  }

  @action
  seek(params: SeekParams) {
    seekMedia(this, params);
  }

  @action
  setVolume(params: SetVolumeParams) {
    setMediaVolume(this, params);
  }

  @action
  setPlaybackRate(params: SetPlaybackRateParams) {
    setMediaPlaybackRate(this, params);
  }

  @action
  setControls(params: SetControlsParams) {
    setMediaControls(this, params);
  }

  @action
  setAutoplay(params: SetAutoplayParams) {
    setMediaAutoplay(this, params);
  }
  // #endregion
}
